import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'

import FileDialog from 'tools/Files/FileDialog'
import { uploadFile } from 'tools/Files/upload'
import Modal from 'tools/Modal'
import { Icon } from 'tools/Uniform/Icons'

import GlobalContext from 'reducer/global'

import style from './index.module.scss'

function PictureEdit({
  image,
  refType,
  refId,
  subject,
  children,
  onSuccess,
  canEdit,
  config,
  className = ''
}) {
  const [edit, setEdit] = useState(false)
  const [error, setError] = useState('')
  // const canEditPic = !!refId
  return (
    <>
      <div className="flex flex-grow-0 flex-shrink-1">
        <div
          className={`relative ${className}`}
          onClick={() => canEdit && setEdit(!edit)}
        >
          {children}
          {canEdit && (
            <button
              className={`${style.bclear} ${className} clear action overlay abs-centerXY flex-center w-100 h-100 ga2 f5 flex-column`}
            >
              <Icon type="edit" />
              <span className="f4">change</span>
            </button>
          )}
        </div>
      </div>
      <Modal width="fw-shrink" viewState={[edit, setEdit]} className="">
        <div className="pa4">
          <Editor
            image={image}
            refType={refType}
            refId={refId}
            subject={subject}
            onSuccess={(v) => {
              setEdit(false)
              onSuccess(v)
            }}
            config={config}
            onFailure={(err) => {
              setError(err)
            }}
          />
          {error && (
            <div className="mt3">
              <b className="warn db mb3">Oops! Something went wrong</b>
              <p />
              We couldn't complete your request because: {error}.
              <p />
              Please try again later — and thanks for your patience!
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

const defaultState = {
  image: '',
  allowZoomOut: false,
  position: { x: 0.5, y: 0.5 },
  scale: 1,
  rotate: 0,
  borderRadius: 0,
  preview: null,
  loaded: false
}

const freeze = Object.freeze

function Editor({
  image,
  refType,
  refId,
  subject,
  onSuccess,
  config,
  onFailure = () => {}
}) {
  const [{ apollo }] = useContext(GlobalContext)
  const editor = useRef()
  const [state, setStateInner] = useState({ ...defaultState, image: image.url })
  const setState = (keypair) => setStateInner({ ...state, ...keypair })
  const setFromEvent = (ev, key) =>
    setState({ [key]: parseFloat(ev.target.value) })
  const mime = 'image/webp'
  const reconfig = useMemo(() => {
    if (typeof config.width === 'number') {
      return freeze({ width: config.width, height: config.height })
    } else {
      if (config.width.includes('rem')) {
        return freeze({
          width: parseInt(config.width) * 16,
          height: parseInt(config.height) * 16
        })
      }
      return freeze({ width: 200, height: 300 })
    }
  }, [config.width, config.height])

  const saveFile = useCallback(() => {
    const canvas = editor.current.getImageScaledToCanvas().toDataURL(mime)

    fetch(canvas)
      .then((res) => res.blob())
      .then((blob) => {
        // TODO: is there an easy way to get the canvas size?
        const size = 1
        uploadFile({
          apollo,
          subject,
          name: 'avatar',
          mime,
          refType,
          refId,
          size,
          src: blob,
          id: undefined,
          refetchQueries: ['loadProject'],
          onSuccess,
          onFailure
        })
      })
  }, [apollo, onFailure, onSuccess, refId, refType, subject])

  // todo: height/width configurable; fix styles

  // redo with fixed size styles. 2x3, 1x1, etc

  // todo: add in uploading
  // https://www.npmjs.com/package/react-avatar-editor
  return (
    <div className="flex flex-column items-center">
      <div>
        <AvatarEditor
          ref={editor}
          scale={state.scale}
          width={reconfig.width || 200}
          height={reconfig.height || 300}
          position={state.position}
          onPositionChange={(position) => setState({ position })}
          rotate={state.rotate}
          image={state.image}
          className="editor-canvas"
        />
      </div>
      <div className="mt3 dib">
        <input
          type="range"
          step="0.01"
          min="0.5"
          max="2"
          name="scale"
          value={state.scale}
          disabled={!state.loaded}
          onChange={(ev) => setFromEvent(ev, 'scale')}
        />
      </div>
      <div className="flex justify-around mt3 w-100 gh3">
        <FileDialog
          idRef="upload-avatar"
          label="Choose File"
          onChange={(e) => setState({ image: e.target.files[0], loaded: true })}
          className={`${state.loaded ? 'border' : ''} button large`}
        />
        <button className="large" onClick={saveFile} disabled={!state.loaded}>
          Save
        </button>
      </div>
    </div>
  )
}

// called as part of normalization
// export function makeAvatarUrl(origin) {
//   const avatar = origin?.avatar
//   if (avatar?.path) {
//     return { url: `${config.pub}${avatar.path}` }
//   } else {
//     return avatarUrl(origin)
//   }
// }

export function avatarUrl(origin) {
  // console.log({ origin })
  return ''
}
// // called after normalization
// export function avatarUrl(origin) {
//   let url = origin?.avatar?.url
//   if (url) {
//     return { url }
//   }
//   let base = 'idea'
//   if (origin.__typename === 'User') {
//     base = 'avatar'
//   }
//   // let loc = parseInt('0x' + (origin?.id || '').slice(28, 36)) % 16
//   // loc = loc ? loc + 1 : 1
//   return { default: true }
// }

export function PictureUpload({
  className = '',
  origin,
  subject,
  onSave,
  Display = PictureView,
  canEdit = true,
  round = true,
  // upload size vs what to show when not editing size
  upload,
  display
}) {
  if (!origin._normal) {
    return null
  }
  return (
    <PictureEdit
      image={origin.avatar}
      subject="avatar"
      onSuccess={onSave}
      refType={subject}
      refId={origin.id}
      canEdit={canEdit}
      className={className}
      config={upload}
    >
      <Display origin={origin} round={round} config={display} />
    </PictureEdit>
  )
}

export function PictureView({ origin, round, config }) {
  const url = origin.avatar?.url || ''
  const style = { maxWidth: config.width, maxHeight: config.height }
  return (
    <div
      className={`${style.image}  ${round ? style.round : ''} nomargin`}
      style={style}
    >
      <img src={url} alt={config.alt} style={style} />
    </div>
  )
}

export default PictureUpload

export { style }
